import apiClient from './apiClient';

/**
 * Service for admin-related API requests
 */
const adminService = {
  /**
   * Get all users with pagination and search
   * @param {object} params - Query parameters
   * @return {Promise} - API response
   */
  getUsers: ({ page = 1, limit = 10, search = '' } = {}) => {
    return apiClient.get(`/admin/users?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`);
  },

  /**
   * Get admin dashboard statistics
   * @return {Promise} - API response
   */
  getStats: () => {
    return apiClient.get('/admin/stats');
  },

  /**
   * Get detailed user information
   * @param {string} userId - User ID
   * @return {Promise} - API response
   */
  getUserDetails: (userId) => {
    return apiClient.get(`/admin/users/${userId}`);
  },

  /**
   * Update user role
   * @param {string} userId - User ID
   * @param {string} role - New role (ADMIN, CUSTOMER, or PRO_CUSTOMER)
   * @return {Promise} - API response
   */
  updateUserRole: (userId, role) => {
    return apiClient.put(`/admin/users/${userId}/role`, { role });
  },

  /**
   * Update user subscription (admin only)
   * @param {string} userId - User ID
   * @param {string} subscription - New subscription type (basic or pro)
   * @return {Promise} - API response
   */
  updateUserSubscription: (userId, subscription) => {
    // Use the self-service upgrade endpoint for pro upgrades
    if (subscription === 'pro') {
      return apiClient.put(`/admin/users/${userId}/upgrade`);
    }
    // Use admin endpoint for other subscription changes
    return apiClient.put(`/admin/users/${userId}/subscription`, { subscription });
  },

  /**
   * Deactivate or delete user
   * @param {string} userId - User ID
   * @param {boolean} permanent - Whether to permanently delete
   * @return {Promise} - API response
   */
  deleteUser: (userId, permanent = false) => {
    return apiClient.delete(`/admin/users/${userId}`, { 
      data: { permanent }
    });
  },

  /**
   * Get subscription analytics
   * @return {Promise} - API response
   */
  getSubscriptionStats: () => {
    return apiClient.get('/admin/subscription-stats');
  },

  /**
   * Get real-time dashboard metrics
   * @return {Promise} - API response with current metrics
   */
  getDashboardMetrics: async () => {
    try {
      const stats = await adminService.getStats();
      
      // Transform the data to match the expected format for the dashboard
      return {
        totalUsers: stats.totalUsers,
        activeUsers: stats.activeUsers,
        totalProperties: stats.totalProperties,
        savedProperties: stats.totalFavorites,
        usersWithFavorites: stats.usersWithFavorites,
        recentFavorites: stats.recentFavorites,
        growthTrends: stats.growthTrends || []
      };
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      throw error;
    }
  }
};

export default adminService; 